import React, { useState } from "react"
import { navigate } from "gatsby"
import { useShoppingCart } from "use-shopping-cart"
import { trackEvent } from "../utils/analytics"
import PLACEHOLDER_IMAGE from "../utils/placeholder"
import { extractPlainText } from "../utils/rich-text"
import "./card.css"

const Card = ({ data }) => {
  const { addItem } = useShoppingCart()
  const [addedItemId, setAddedItemId] = useState(null)
  const [activeFilter, setActiveFilter] = useState("todos")

  const edges = data.allContentfulProducto.edges
  const categories = [
    ...new Set(edges.map((e) => e.node.categoria).filter(Boolean)),
  ].sort()
  const brands = [
    ...new Set(edges.map((e) => e.node.marca).filter(Boolean)),
  ].sort()

  const filterOptions = [
    { key: "todos", label: "Todos" },
    ...categories.map((c) => ({ key: `cat:${c}`, label: c })),
    ...brands.map((b) => ({ key: `brand:${b}`, label: b })),
  ]

  const filteredEdges = edges.filter((edge) => {
    if (activeFilter === "todos") return true
    if (activeFilter.startsWith("cat:"))
      return edge.node.categoria === activeFilter.slice(4)
    if (activeFilter.startsWith("brand:"))
      return edge.node.marca === activeFilter.slice(6)
    return true
  })

  const buildProduct = (edge) => {
    const description = edge.node.descripcion?.raw
      ? extractPlainText(edge.node.descripcion.raw)
      : edge.node.nombre || ""
    const discount = edge.node.descuento || 0
    const price =
      discount > 0
        ? Math.round(edge.node.precio * (1 - discount / 100) * 100)
        : Math.round(edge.node.precio * 100)
    return {
      id: edge.node.codigo,
      name: edge.node.nombre,
      description: description,
      price: price,
      image:
        edge.node.imagenes && edge.node.imagenes[0]
          ? edge.node.imagenes[0].file.url
          : "",
      currency: "MXN",
    }
  }

  const handleAddToCart = (e, edge) => {
    e.preventDefault()
    e.stopPropagation()
    const product = buildProduct(edge)
    addItem(product)
    trackEvent("add_to_cart", {
      currency: "MXN",
      value: product.price / 100,
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          price: product.price / 100,
        },
      ],
    })
    setAddedItemId(edge.node.codigo)
    setTimeout(() => setAddedItemId(null), 1500)
  }

  const handleCardClick = (e, codigo) => {
    if (!e.target.closest(".CardAddToCart")) {
      navigate(`/productos/${codigo}`)
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      minimumFractionDigits: 0,
    }).format(price)
  }

  return (
    <div className="Cards" id="productos">
      <div className="CardsHeader">
        <h2>Nuestros Productos</h2>
        <p className="CardsSubtitle">Encuentra lo que necesitas</p>
        {filterOptions.length > 1 && (
          <div className="CardFilters">
            {filterOptions.map((opt) => (
              <button
                key={opt.key}
                className={`CardFilterBtn ${
                  activeFilter === opt.key ? "CardFilterBtnActive" : ""
                }`}
                onClick={() => setActiveFilter(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="CardGroup">
        {filteredEdges.map((edge, index) => {
          const description = edge.node.descripcion?.raw
            ? extractPlainText(edge.node.descripcion.raw)
            : ""
          const isAdded = addedItemId === edge.node.codigo

          return (
            <div
              className="Card"
              key={edge.node.codigo}
              onClick={(e) => handleCardClick(e, edge.node.codigo)}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="CardImageContainer">
                <img
                  src={edge.node.imagenes?.[0]?.file?.url || PLACEHOLDER_IMAGE}
                  alt={edge.node.nombre}
                  className="CardImage"
                />
                <div className="CardImageOverlay"></div>
                {edge.node.descuento >= 15 && (
                  <span className="CardDiscountBadge">
                    -{edge.node.descuento}%
                  </span>
                )}
              </div>
              <div className="CardContent">
                <h3 className="CardTitle">{edge.node.nombre}</h3>
                {description && (
                  <p className="CardDescription">{description}</p>
                )}
                {edge.node.descuento > 0 ? (
                  <div className="CardPriceGroup">
                    <span className="CardPriceOriginal">
                      {formatPrice(edge.node.precio)}
                    </span>
                    <span className="CardPrice CardPriceDiscount">
                      {formatPrice(
                        Math.round(
                          edge.node.precio * (1 - edge.node.descuento / 100)
                        )
                      )}
                    </span>
                  </div>
                ) : (
                  <div className="CardPrice">
                    {formatPrice(edge.node.precio)}
                  </div>
                )}
                <div className="CardActions">
                  <button
                    className={`CardAddToCart ${
                      isAdded ? "CardAddToCartAdded" : ""
                    }`}
                    onClick={(e) => handleAddToCart(e, edge)}
                    disabled={isAdded}
                  >
                    {isAdded ? "Agregado ✓" : "Agregar al Carrito"}
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default Card
